'use client';

import Image from 'next/image';
import { useState } from 'react';
import MediaPlaceholder from '@/components/ui/MediaPlaceholder';

/** Renders a poster + play button and only mounts the YouTube iframe on click.
 *  Saves ~2 MB of YouTube player JS on initial load (Lighthouse "3rd parties").
 *  Returns null if `src` looks like an unfilled placeholder (e.g. YOUR_VIDEO_ID). */
export default function YouTubeFacade({
  src,
  title,
  poster,
  posterAlt,
  className = '',
  iframeClassName = 'absolute inset-0 h-full w-full rounded-lg',
  sizes = '(max-width: 1024px) 100vw, 50vw',
}: {
  src: string;
  title: string;
  poster: string;
  posterAlt: string;
  className?: string;
  iframeClassName?: string;
  sizes?: string;
}) {
  const [playing, setPlaying] = useState(false);

  const hasPoster = Boolean(poster);

  if (!src || src.includes('YOUR_VIDEO_ID')) {
    return (
      <div className={`relative h-full w-full bg-neutral-10 ${className}`}>
        {hasPoster ? (
          <Image src={poster} alt={posterAlt} fill className="object-cover" sizes={sizes} />
        ) : (
          <MediaPlaceholder />
        )}
      </div>
    );
  }

  const autoplaySrc = src.includes('autoplay=')
    ? src
    : `${src}${src.includes('?') ? '&' : '?'}autoplay=1`;

  return (
    <div className={`relative h-full w-full bg-neutral-10 ${className}`}>
      {playing ? (
        <iframe
          className={iframeClassName}
          src={autoplaySrc}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <>
          {hasPoster ? (
            <Image src={poster} alt={posterAlt} fill className="object-cover" sizes={sizes} />
          ) : (
            <MediaPlaceholder />
          )}
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play video: ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors hover:bg-black/20 focus-visible:bg-black/20 focus-visible:outline-none"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-blue-100 shadow-md backdrop-blur-sm sm:h-[72px] sm:w-[72px]">
              <svg viewBox="0 0 24 24" className="ml-1 h-8 w-8" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        </>
      )}
    </div>
  );
}
